import {FC, useState} from 'react'
import {createPortal} from 'react-dom'
import axios from 'axios'
import {useAuth} from '../../../../app/modules/auth/core/Auth'

const API_URL = process.env.REACT_APP_API_URL || ''

// A stale/expired admin_auth_token can linger in a client's browser (e.g. a shared
// machine), so presence alone isn't enough — decode the JWT and require an admin role
// that hasn't expired. Same check AdminIssuesWidget uses.
const isValidAdminToken = (token: string | null): boolean => {
  if (!token) return false
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (payload?.role !== 'admin') return false
    if (payload?.exp && payload.exp * 1000 <= Date.now()) return false
    return true
  } catch {
    return false
  }
}

const fmt = (d: string | Date | null) => (d ? new Date(d).toLocaleDateString('he-IL') : 'ללא')

// "תקופת מנוי" on the header — the same button and popup as the admin account page,
// so an admin can add or remove days without leaving the customer's account.
//
// Shown only for a valid admin JWT. That token survives the switch into a client
// account (which is what makes impersonation work) and is what authenticates the call:
// at this point the browser session belongs to the CUSTOMER, so a session-role check
// would refuse the admin or attribute the audit entry to the wrong user. The server
// side (POST /admin/account-days/:id) guards on the same token.
const AdminSubscriptionDaysWidget: FC = () => {
  const {currentUser} = useAuth()
  const adminToken = (() => {
    const raw = localStorage.getItem('admin_auth_token')
    return isValidAdminToken(raw) ? raw : null
  })()

  const accountId = (currentUser as any)?._id || (currentUser as any)?.id
  const accountName = [(currentUser as any)?.first_name, (currentUser as any)?.last_name]
    .filter(Boolean).join(' ').trim() || (currentUser as any)?.email || ''

  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'days' | 'date'>('days')
  const [days, setDays] = useState(30)
  const [date, setDate] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [trialEnd, setTrialEnd] = useState<string | null>(null)

  if (!adminToken || !accountId) return null

  const currentEnd = trialEnd || (currentUser as any)?.trial_period_end || null

  // Mirrors the server: days are added to whichever is later, the current end or now,
  // so days given to a lapsed account count from today rather than from a past date.
  const preview = (() => {
    if (mode === 'date') return date ? new Date(date) : null
    const base = currentEnd && new Date(currentEnd) > new Date() ? new Date(currentEnd) : new Date()
    const d = new Date(base)
    d.setDate(d.getDate() + (days || 0))
    return d
  })()

  const save = async () => {
    if (mode === 'days' && !days) return
    if (mode === 'date' && !date) return
    setBusy(true)
    try {
      const res = await axios.post(
        `${API_URL}/admin/account-days/${accountId}`,
        mode === 'days' ? {days, reason} : {new_date: date, reason},
        {headers: {Authorization: `Bearer ${adminToken}`}, withCredentials: true}
      )
      if (res.data?.success) {
        setTrialEnd(res.data.trial_period_end)
        setOpen(false)
        setReason('')
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || 'עדכון תקופת המנוי נכשל')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={currentEnd ? `תקופת מנוי — עד ${fmt(currentEnd)}` : 'תקופת מנוי'}
        style={{
          padding: '6px 12px',
          borderRadius: '8px',
          border: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '11px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          marginInlineEnd: '8px',
        }}
      >
        <i className='bi bi-calendar-plus' style={{fontSize: '12px'}}></i>
        תקופת מנוי
      </button>

      {open && createPortal(
        <div
          onClick={() => !busy && setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            dir='rtl'
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16,
              padding: 24, border: '1px solid #e4e6ef', boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
            }}
          >
            <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20}}>
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className='bi bi-calendar-plus' style={{fontSize: 18, color: '#fff'}}></i>
              </div>
              <div>
                <h3 style={{margin: 0, fontSize: 16, fontWeight: 700, color: '#181c32'}}>שינוי תקופת מנוי</h3>
                <p style={{margin: '2px 0 0', fontSize: 12, color: '#a1a5b7'}}>{accountName}</p>
              </div>
            </div>

            <div style={{display: 'flex', gap: 8, marginBottom: 16}}>
              {(['days', 'date'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  style={{
                    flex: 1, padding: 10, borderRadius: 8,
                    border: `1px solid ${mode === m ? '#667eea' : '#e4e6ef'}`,
                    background: mode === m ? 'rgba(102, 126, 234, 0.1)' : '#f9f9f9',
                    color: mode === m ? '#667eea' : '#5e6278',
                    cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  }}
                >
                  {m === 'days' ? 'הוספת ימים' : 'בחירת תאריך'}
                </button>
              ))}
            </div>

            {mode === 'days' ? (
              <div style={{marginBottom: 16}}>
                <label style={{display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#5e6278'}}>
                  מספר ימים (שלילי להורדה)
                </label>
                <input
                  type='number'
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value) || 0)}
                  style={{width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e4e6ef', fontSize: 14}}
                />
              </div>
            ) : (
              <div style={{marginBottom: 16}}>
                <label style={{display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#5e6278'}}>
                  תאריך סיום חדש
                </label>
                <input
                  type='date'
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e4e6ef', fontSize: 14}}
                />
              </div>
            )}

            <div style={{
              background: '#f9f9f9', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 12,
            }}>
              <span style={{color: '#a1a5b7'}}>מ־{fmt(currentEnd)}</span>
              <span style={{margin: '0 8px', color: '#a1a5b7'}}>←</span>
              <span style={{fontWeight: 700, color: '#181c32'}}>{preview ? fmt(preview) : '—'}</span>
            </div>

            <div style={{marginBottom: 20}}>
              <label style={{display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#5e6278'}}>
                סיבה (רשות)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder='נרשם ביומן השינויים של הלקוח'
                style={{width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e4e6ef', fontSize: 13, resize: 'vertical'}}
              />
            </div>

            <div style={{display: 'flex', gap: 8}}>
              <button
                onClick={() => setOpen(false)}
                disabled={busy}
                style={{flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e4e6ef', background: '#fff', color: '#5e6278', cursor: 'pointer', fontSize: 13, fontWeight: 600}}
              >
                ביטול
              </button>
              <button
                onClick={save}
                disabled={busy || (mode === 'days' ? !days : !date)}
                style={{
                  flex: 2, padding: 10, borderRadius: 8, border: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff', cursor: busy ? 'default' : 'pointer', fontSize: 13, fontWeight: 700, opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? 'שומר…' : 'שמור'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export {AdminSubscriptionDaysWidget}
